import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./PlaylistPage.css";
import "./ExportDialog.css";
import apiClient from "../api/client";
import { parseDateStr, toDateId as formatDateStr } from "../utils/date";

const WEEKDAY_LABELS = ["日", "一", "二", "三", "四", "五", "六"];
const ROW_HEADER = ["日期", "星期", "聆聽時長(分)", "歌曲", "評分", "不良反應"];

function formatSurveyCell(survey) {
  const symptoms = [];
  if (survey.q1_yes) symptoms.push(`刺耳感(${survey.q1_severity})`);
  if (survey.q2_yes) symptoms.push(`耳朵痛(${survey.q2_severity})`);
  if (survey.q3_yes) symptoms.push(`頭痛(${survey.q3_severity})`);
  if (survey.q4_yes) {
    const otherText = survey.q4_text ? `: ${survey.q4_text}` : "";
    symptoms.push(`其他不適(${survey.q4_severity})${otherText}`);
  }
  return symptoms.length > 0 ? symptoms.join("; ") : "無不適";
}

function sanitizeSheetName(name) {
  return name.replace(/[[\]:\\/?*]/g, "_").slice(0, 31) || "sheet";
}

async function buildUserRows(username, startDate, endDate) {
  const [listeningRes, surveyRes] = await Promise.all([
    apiClient.get(`/api/users/${username}/listening-history`),
    apiClient.get(`/api/users/${username}/adverse-surveys`),
  ]);

  const listeningByDate = {};
  (listeningRes.data || []).forEach((entry) => {
    listeningByDate[entry.date] = entry;
  });

  const surveyByDate = {};
  (surveyRes.data || []).forEach((survey) => {
    surveyByDate[survey.date_id] = survey;
  });

  const rows = [];
  const cursor = parseDateStr(startDate);
  const end = parseDateStr(endDate);
  while (cursor <= end) {
    const dateStr = formatDateStr(cursor);
    const listening = listeningByDate[dateStr];
    const survey = surveyByDate[dateStr];
    const minutes = listening ? Math.floor((listening.minutes / 60) * 100) / 100 : 0;
    const hasRating = listening && listening.rating !== null && listening.rating !== undefined;

    rows.push([
      dateStr,
      WEEKDAY_LABELS[cursor.getDay()],
      minutes,
      listening && listening.song_id ? listening.song_id : "無",
      hasRating ? listening.rating : "-",
      survey ? formatSurveyCell(survey) : "未填寫",
    ]);

    cursor.setDate(cursor.getDate() + 1);
  }
  return rows;
}

function TriStateCheckbox({ state, onChange }) {
  return (
    <input
      type="checkbox"
      checked={state === "all"}
      ref={(el) => {
        if (el) el.indeterminate = state === "partial";
      }}
      onChange={onChange}
    />
  );
}

function groupSelectionState(members, selected) {
  if (members.length === 0) return "none";
  const selectedCount = members.filter((m) => selected.has(m)).length;
  if (selectedCount === 0) return "none";
  if (selectedCount === members.length) return "all";
  return "partial";
}

function ExportDialog({ visible, onClose, playlistGroups }) {
  const [treeGroups, setTreeGroups] = useState([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState([]);
  const [selectedUsernames, setSelectedUsernames] = useState(new Set());
  const [isLoadingTree, setIsLoadingTree] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const groups = (playlistGroups || []).filter((g) => g.id !== "all");

  useEffect(() => {
    if (!visible) return;
    setExpandedGroupIds([]);
    setSelectedUsernames(new Set());
    setStartDate("");
    setEndDate("");

    const fetchTree = async () => {
      setIsLoadingTree(true);
      try {
        const [usersRes, ...groupUsersRes] = await Promise.all([
          apiClient.get("/api/manager/users"),
          ...groups.map((g) => apiClient.get(`/api/groups/${g.id}/users`)),
        ]);

        const built = groups.map((g, i) => ({
          id: g.id,
          name: g.name,
          members: groupUsersRes[i].data || [],
        }));

        const groupedUsernames = new Set(built.flatMap((g) => g.members));
        const allUsernames = (usersRes.data || []).map((u) => u.username);
        const ungrouped = allUsernames.filter((u) => !groupedUsernames.has(u));
        if (ungrouped.length > 0) {
          built.push({ id: "__ungrouped__", name: "未分組", members: ungrouped });
        }

        setTreeGroups(built);
      } catch (err) {
        console.error("取得使用者/組別清單失敗", err);
        setTreeGroups([]);
      } finally {
        setIsLoadingTree(false);
      }
    };
    fetchTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  const allUsernames = treeGroups.flatMap((g) => g.members);

  const toggleExpand = (groupId) => {
    setExpandedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const toggleUser = (username) => {
    setSelectedUsernames((prev) => {
      const next = new Set(prev);
      if (next.has(username)) next.delete(username);
      else next.add(username);
      return next;
    });
  };

  const toggleGroup = (group) => {
    const state = groupSelectionState(group.members, selectedUsernames);
    setSelectedUsernames((prev) => {
      const next = new Set(prev);
      if (state === "all") {
        group.members.forEach((m) => next.delete(m));
      } else {
        group.members.forEach((m) => next.add(m));
      }
      return next;
    });
  };

  const toggleAll = () => {
    const state = groupSelectionState(allUsernames, selectedUsernames);
    setSelectedUsernames(state === "all" ? new Set() : new Set(allUsernames));
  };

  const isDateRangeValid = startDate && endDate && startDate <= endDate;
  const canExport = isDateRangeValid && selectedUsernames.size > 0 && !isExporting;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const usernames = Array.from(selectedUsernames).sort();
      if (usernames.length === 0) {
        alert("沒有符合條件的使用者");
        return;
      }

      // 每個使用者的資料互不依賴，平行抓取比逐一 await 快上 n 倍
      // （選「全部使用者」時人數一多，逐一等待會拖得很久）。
      const perUserRows = {};
      const rowsPerUser = await Promise.all(
        usernames.map((username) => buildUserRows(username, startDate, endDate))
      );
      usernames.forEach((username, i) => {
        perUserRows[username] = rowsPerUser[i];
      });

      const workbook = XLSX.utils.book_new();

      const summarySheetData = [["使用者", ...ROW_HEADER]];
      usernames.forEach((username) => {
        perUserRows[username].forEach((row) => {
          summarySheetData.push([username, ...row]);
        });
      });
      const summarySheet = XLSX.utils.aoa_to_sheet(summarySheetData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "總表");

      const usedSheetNames = new Set(["總表"]);
      usernames.forEach((username) => {
        const sheetData = [ROW_HEADER, ...perUserRows[username]];
        const sheet = XLSX.utils.aoa_to_sheet(sheetData);
        let sheetName = sanitizeSheetName(username);
        while (usedSheetNames.has(sheetName)) {
          sheetName = sanitizeSheetName(`${username}_`.slice(0, 30) + usedSheetNames.size);
        }
        usedSheetNames.add(sheetName);
        XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
      });

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(data, `使用者資料_${startDate}_${endDate}.xlsx`);
      onClose();
    } catch (error) {
      console.error("匯出 Excel 失敗:", error);
      alert("匯出 Excel 失敗，請稍後再試");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog-box export-dialog-box">
        <h3>📊 匯出使用者資料</h3>

        <div className="export-section">
          <div className="export-section-title">匯出範圍</div>
          {isLoadingTree ? (
            <div className="export-empty-hint">載入使用者清單中...</div>
          ) : treeGroups.length === 0 ? (
            <div className="export-empty-hint">目前沒有可選的使用者</div>
          ) : (
            <div className="export-tree">
              <label className="export-tree-row export-tree-row-root">
                <TriStateCheckbox
                  state={groupSelectionState(allUsernames, selectedUsernames)}
                  onChange={toggleAll}
                />
                全部使用者
              </label>
              {treeGroups.map((g) => {
                const expanded = expandedGroupIds.includes(g.id);
                const state = groupSelectionState(g.members, selectedUsernames);
                return (
                  <div key={g.id}>
                    <div className="export-tree-row">
                      <button
                        type="button"
                        className="export-tree-toggle"
                        onClick={() => toggleExpand(g.id)}
                        aria-label={expanded ? "收合" : "展開"}
                      >
                        {expanded ? "▼" : "▶"}
                      </button>
                      <label className="export-tree-row-label">
                        <TriStateCheckbox state={state} onChange={() => toggleGroup(g)} />
                        {g.name}（{g.members.length}人）
                      </label>
                    </div>
                    {expanded && (
                      <div className="export-tree-children">
                        {g.members.map((username) => (
                          <label key={username} className="export-tree-row">
                            <input
                              type="checkbox"
                              checked={selectedUsernames.has(username)}
                              onChange={() => toggleUser(username)}
                            />
                            {username}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="export-section">
          <div className="export-section-title">日期區間</div>
          <div className="export-date-row">
            <label>
              起始日期
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </label>
            <label>
              結束日期
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>
          </div>
          {startDate && endDate && startDate > endDate && (
            <div className="export-empty-hint">起始日期不能晚於結束日期</div>
          )}
        </div>

        <div className="dialog-actions">
          <button onClick={handleExport} disabled={!canExport}>
            {isExporting ? "匯出中..." : "確認匯出"}
          </button>
          <button onClick={onClose} disabled={isExporting}>
            取消
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExportDialog;
